import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditTaskDialog = ({ task, users, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: task.title || '',
    description: task.description || '',
    assigned_to: task.assigned_to || '',
    customer_name: task.customer_name || '',
    customer_details: task.customer_details || '',
    deadline_hours: task.deadline_hours || '',
    status: task.status || 'pending'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.assigned_to || !formData.deadline_hours) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };

      await axios.patch(`${API}/tasks/${task.id}`, formData, config);
      toast.success('Task updated successfully');
      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1"
          data-testid={`edit-task-btn-${task.id}`}
        >
          <Edit className="w-3 h-3" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="edit-task-dialog">
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Task Title *</label>
              <Input 
                data-testid="edit-task-title-input"
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})} 
                placeholder="Enter task title"
                required
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Textarea 
                data-testid="edit-task-description-input"
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})} 
                placeholder="Enter task description"
                required
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Assign To *</label>
              <Select 
                value={formData.assigned_to} 
                onValueChange={(value) => setFormData({...formData, assigned_to: value})}
                disabled={submitting}
              >
                <SelectTrigger data-testid="edit-task-assign-select">
                  <SelectValue placeholder="Select a field staff" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.role === 'field_staff').map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <Select 
                value={formData.status} 
                onValueChange={(value) => setFormData({...formData, status: value})}
                disabled={submitting}
              >
                <SelectTrigger data-testid="edit-task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Customer Name (Optional)</label>
              <Input 
                data-testid="edit-task-customer-input"
                value={formData.customer_name} 
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})} 
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Customer Details (Optional)</label>
              <Textarea 
                data-testid="edit-task-customer-details-input"
                value={formData.customer_details} 
                onChange={(e) => setFormData({...formData, customer_details: e.target.value})} 
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Deadline (Hours) *</label>
              <Input 
                type="number"
                placeholder="Hours from creation"
                data-testid="edit-task-deadline-input"
                value={formData.deadline_hours} 
                onChange={(e) => setFormData({...formData, deadline_hours: e.target.value})} 
                min="1"
                required
                disabled={submitting}
              />
              <p className="text-xs text-slate-500 mt-1">Enter deadline in hours (e.g., 24 for 1 day, 168 for 1 week)</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Task'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTaskDialog;
